import type { FlashHotelCatalogConfig, RewardsCatalog } from '~/services/LuxLoyalty';

/**
 * A reward catalog shape with the raw (not yet contract-typed) `config` field layered on.
 * TODO(LOYAL-2863): the generated svc-lux-loyalty contract hasn't caught up with the catalog
 * `config` field yet, so it's widened locally here rather than trusting a field the contract
 * doesn't promise. `config` is `unknown` because the API only guarantees it's a JSON object (or
 * `null`) — `flashConfigState` below is what narrows it.
 */
export type FlashHotelCatalog = RewardsCatalog & { config?: unknown };

/** Inclusive numeric bounds (and whole-number requirement) for one Flash Config field. */
interface FieldBounds {
  min: number;
  /** Omitted when the field has no upper bound. */
  max?: number;
  integer: boolean;
  /** Human-readable description of the valid range, used in both error and helper text. */
  rangeLabel: string;
}

/**
 * Bounds for the hotel_flash catalog's 9 pricing/display constants, per loyalty's
 * `src/lib/rewards/flashHotelConfig.ts` (the schema's authority — keep these in sync with it):
 * copayPercentage and pointsCoveragePercentage are 0-100 percentages, burnRateAmplifier divides
 * burn points (`points = burn / amplifier`) and ranges 1-10, minMarginPercent caps out at 75 (a
 * 100% minimum margin would make every package unsellable), vppFloorCents/vppCeilingCents are
 * 0-100 cent bounds on value-per-point (with a cross-field floor <= ceiling rule enforced
 * separately — see `validateFlashConfig`), and the three count-like fields (pointsRounding,
 * defaultAllocation, minRoomsRemaining) must be whole numbers.
 */
const FIELD_BOUNDS: Record<keyof FlashHotelCatalogConfig, FieldBounds> = {
  copayPercentage: { min: 0, max: 100, integer: false, rangeLabel: '0-100' },
  pointsCoveragePercentage: { min: 0, max: 100, integer: false, rangeLabel: '0-100' },
  burnRateAmplifier: { min: 1, max: 10, integer: false, rangeLabel: '1-10' },
  minMarginPercent: { min: 0, max: 75, integer: false, rangeLabel: '0-75' },
  pointsRounding: { min: 1, integer: true, rangeLabel: 'a whole number ≥ 1' },
  vppFloorCents: { min: 0, max: 100, integer: false, rangeLabel: '0-100' },
  vppCeilingCents: { min: 0, max: 100, integer: false, rangeLabel: '0-100' },
  defaultAllocation: { min: 0, integer: true, rangeLabel: 'a whole number ≥ 0' },
  minRoomsRemaining: { min: 0, integer: true, rangeLabel: 'a whole number ≥ 0' },
};

/** Stable field order the form and validator both iterate in. */
const FIELD_ORDER: ReadonlyArray<keyof FlashHotelCatalogConfig> = [
  'copayPercentage',
  'pointsCoveragePercentage',
  'burnRateAmplifier',
  'minMarginPercent',
  'pointsRounding',
  'vppFloorCents',
  'vppCeilingCents',
  'defaultAllocation',
  'minRoomsRemaining',
];

/** Helper text shown under each Flash Config field, describing its valid range. */
export const FLASH_CONFIG_FIELD_HELPER_TEXT: Record<keyof FlashHotelCatalogConfig, string> = FIELD_ORDER.reduce(
  (acc, field) => {
    acc[field] = `Must be ${FIELD_BOUNDS[field].rangeLabel}`;
    return acc;
  },
  {} as Record<keyof FlashHotelCatalogConfig, string>,
);

/** Result of validating the Flash Config form: `config` is only set when `errors` is empty. */
export interface ValidateFlashConfigResult {
  config?: FlashHotelCatalogConfig;
  errors: Partial<Record<keyof FlashHotelCatalogConfig, string>>;
}

/**
 * Validates and parses the Flash Config form's 9 string fields into a `FlashHotelCatalogConfig`.
 *
 * Pure function, no side effects — safe to call on every keystroke. Each field is first checked
 * independently against its bound in `FIELD_BOUNDS`: blank or non-numeric input, a fractional
 * value where an integer is required, or a value outside the field's inclusive range all produce
 * an entry in `errors` keyed by field name. A second, cross-field pass then checks that
 * `vppFloorCents` does not exceed `vppCeilingCents` — but only when both parsed individually
 * clean, so a single out-of-range field doesn't also trigger a confusing cross-field message.
 * Because the API replaces all 9 constants together in one PATCH, `config` is only populated when
 * every check passes — a single invalid field (or an inverted floor/ceiling pair) is enough to
 * withhold it, even though the other fields may be individually valid (no entry in `errors`).
 *
 * @param values Raw string input for each of the 9 numeric config fields, keyed by field name.
 * @returns `{ config, errors }` — `config` is present only when `errors` is empty.
 */
export function validateFlashConfig(values: Record<keyof FlashHotelCatalogConfig, string>): ValidateFlashConfigResult {
  const errors: Partial<Record<keyof FlashHotelCatalogConfig, string>> = {};
  const parsed: Partial<FlashHotelCatalogConfig> = {};

  for (const field of FIELD_ORDER) {
    const bounds = FIELD_BOUNDS[field];
    const trimmed = (values[field] ?? '').trim();
    const numeric = Number(trimmed);

    if (trimmed === '' || Number.isNaN(numeric)) {
      errors[field] = 'Must be a number';
      continue;
    }
    if (bounds.integer && !Number.isInteger(numeric)) {
      errors[field] = `Must be ${bounds.rangeLabel}`;
      continue;
    }
    if (numeric < bounds.min || (bounds.max !== undefined && numeric > bounds.max)) {
      errors[field] = `Must be ${bounds.rangeLabel}`;
      continue;
    }

    parsed[field] = numeric;
  }

  // Cross-field rule: the VPP floor can't sit above the ceiling. Only enforced once both fields
  // have individually passed their own bound checks, so this never masks a plain out-of-range error.
  if (
    errors.vppFloorCents === undefined &&
    errors.vppCeilingCents === undefined &&
    parsed.vppFloorCents !== undefined &&
    parsed.vppCeilingCents !== undefined &&
    parsed.vppFloorCents > parsed.vppCeilingCents
  ) {
    const message = 'vppFloorCents must not exceed vppCeilingCents';
    errors.vppFloorCents = message;
    errors.vppCeilingCents = message;
  }

  if (Object.keys(errors).length > 0) {
    return { errors };
  }

  return { config: parsed as FlashHotelCatalogConfig, errors };
}

/** The hotel_flash config as the admin portal sees it: still loading, loaded in full, or unusable. */
export type FlashConfigState =
  | { status: 'loading' }
  | { status: 'ready'; config: FlashHotelCatalogConfig }
  | { status: 'error'; message: string };

/** The parts of `useFlashHotelCatalog()`'s query result that `flashConfigState` reads. */
interface FlashCatalogQueryLike {
  isLoading: boolean;
  error: unknown;
  /** The active catalog, `null` when none exists, `undefined` before the first response. */
  data: object | null | undefined;
}

/**
 * Reads the hotel_flash config from the catalog query. The portal has no defaults of its own:
 * loyalty returns the effective config (stored values merged with its defaults), so a config
 * that is absent or missing any of the 9 numeric fields is an error to show, never something
 * to fill in locally.
 */
export function flashConfigState(query: FlashCatalogQueryLike): FlashConfigState {
  if (query.isLoading) {
    return { status: 'loading' };
  }
  if (query.error) {
    const reason = query.error instanceof Error ? query.error.message : 'unknown error';
    return { status: 'error', message: `Couldn't load the flash config: ${reason}` };
  }
  if (!query.data) {
    return {
      status: 'error',
      message:
        "No active hotel_flash reward catalog exists. It is seeded by the loyalty service's database migration, so check that the migration has run in this environment.",
    };
  }

  const rawConfig = (query.data as FlashHotelCatalog).config;
  if (typeof rawConfig !== 'object' || rawConfig === null || Array.isArray(rawConfig)) {
    return { status: 'error', message: "The flash config is missing from loyalty's response." };
  }
  const source = rawConfig as Partial<Record<keyof FlashHotelCatalogConfig, unknown>>;
  const invalidFields = FIELD_ORDER.filter(
    (field) => typeof source[field] !== 'number' || !Number.isFinite(source[field]),
  );
  if (invalidFields.length > 0) {
    return {
      status: 'error',
      message: `The flash config is invalid. Missing or not a number: ${invalidFields.join(', ')}.`,
    };
  }
  return { status: 'ready', config: source as FlashHotelCatalogConfig };
}
